import { Injectable } from '@angular/core';
import { PolicyTestResponse, UserClaims } from '@mitel/cloudlink-sdk/authentication';
import { AuthService } from './auth.service';
import { AdminService } from './admin.service';
import { AccountsService } from './accounts.service';
import { Statement, Account } from '@mitel/cloudlink-sdk/admin';
import { PolicyEffects } from '../constants/constants';
import { XLaunchService } from './x-launch.service';
import { FeatureKeyMap, FeatureFlags, TypedPolicyFeatureFlags } from '../interfaces/feature-flags';
import { Subject } from 'rxjs';

// NOTE: This is the centralized management of the feature flags for the accounts application
// It contains a mixture of the old way (quick key enablement and morgan created policies) and the new way (policy)
// Features that are demos, POC, deprioritized, not in trials or early development are and will remain as quick key controlled
// Features that are in trials will be covered by policy access

class FeatureState {
	policyEnabled: boolean;
	defaultEnabled: boolean;
	enabled: boolean;
  }

// NOTE: These policy statements are a mixture of morgan created feature policies, real feature policies (ie managed accounts)
// and quick key feature policies. 

// this policy state will turn on the CC Integration feature so the current logged in user can use it
// not to be mistaken with the polices turned on by the CC-next integrations
const CCIntegrationFeatureStatement: Statement =  {
	statementId: 'cc-integration',
	action: [
		'*'
	],
	resource: [
		"https://mitel.io/auth/console-accounts/features/ccintegrations"
	],
	effect: 'Allow' as const
};

const APIFeatureStatement: Statement =  {
	statementId: 'api',
	action: [
		'*'
	],
	resource: [

		"https://mitel.io/auth/console-accounts/features/api"
	],
	effect: 'Allow' as const
};

@Injectable({
	providedIn: 'root'
})

// this service will hide whatever implementation we eventually have for feature toggles
// right now it is static and saved in the environment file
export class FeatureToggleService {
	private featureStates: Map<string, FeatureState>;
	private initialized = false;
	public flagsUpdated$ = new Subject<void>();

	constructor( private authSvc: AuthService,
		private adminService: AdminService,
		private accountsService: AccountsService,
		private xlaunchService: XLaunchService ) 
	{
		this.featureStates = new Map();
	}

	async initAllFeatureFlags( claims: UserClaims ): Promise<void>
	{
		if ( !this.initialized ) 
		{
			this.initialized = true;
			// Create the featureStates default first
			FeatureKeyMap.forEach((f) => {
				this.featureStates.set(f.featureFlag, {
					policyEnabled: false,
					defaultEnabled: false,
					enabled: f.default,
				});
			});
			try {
				// these are the legacy flags + proximus flags AND the new policy feature flags
				await this.initPolicyTestFlags();
			} 
			catch (ex) {
				console.error('Error initializing POLICY feature flags', ex);
				this.initialized = false;
			}
			this.setupFeatureFlags();	// setup what we have. Currently default is false so less features for the user
			this.flagsUpdated$.next();
		}		
	}

	// This is called once we have our initial policy information to publish the flags
	// We set the flag into for widgets and user util (in the future we might just point them back here)
	private async setupFeatureFlags() {
		// @ts-ignore
		for (const [key, feature] of this.featureStates) {
			feature.enabled = feature.policyEnabled || feature.defaultEnabled;
		}
	}

	/* not writing tests for temporary code*/
	// note: I have added the typed policy checking to the legacy policy checking 
	// so that there is one call to the policy-test api. This was done for performance reasons
	initPolicyTestFlags(): Promise<void>
	{
		// these are the legacy feature flags PLUS standard required flags for features
    const scim = {
      resource: "https://mitel.io/auth/console-accounts/licenses/scim",
      action: "show"
		};
		const oktasso = {
      resource: "https://mitel.io/auth/console-accounts/licenses/oktasso",
      action: "show"
		};
		const ccnext = {
      resource: "https://mitel.io/auth/console-accounts/features/ccintegrations",
      action: "show"
		};
		const api = {
      resource: "https://mitel.io/auth/console-accounts/features/api",
      action: "show"
		};
    const managedAccounts = {
      resource: "https://mitel.io/auth/console-accounts/licenses/managed-accounts",
      action: "show"
		};
		const updateRestrictedAccounts = {
			resource: "https://mitel.io/auth/admin/features/partner-account-restrictions",
      action: "Update"
		};
		const updateRestrictedPolicies = {
			resource: "https://mitel.io/auth/admin/features/restricted-policies",
      action: "Update"
		};
		const gsuitedemo = {
      resource: "https://mitel.io/auth/console-accounts/licenses/gsuitedemo",
      action: "show"
		};
		const subPartner = {
      resource: "https://mitel.io/auth/admin/features/accounts",
      action: "show"
		};

		let feature = {
			resources: [
        scim,
				managedAccounts,
				oktasso,
				updateRestrictedAccounts,
				updateRestrictedPolicies,
				ccnext,
				gsuitedemo, 
				api,
				subPartner
			],
			types:['feature-flag'] // required for new typed policies
		};

		// get the list of generated resources for typed policies
		let typedFeatures = this.getTypedFeatures();

		for( var typedFeature of typedFeatures )
		{
			feature.resources.push(typedFeature.policy);
		}

		return this.authSvc.getPolicyForFeature(feature).then( async r => {
			console.log("This is the policy reply (flags)", r);
			let resp = r;
			if ( resp && resp.results )
			{
        const scimResponse = resp.results.find(res => {
            return res.resource === scim.resource;
        });
        if (scimResponse && PolicyEffects[scimResponse.effect] === PolicyEffects.ExplicitAllow) {
					let oSyncEntry = this.featureStates.get(FeatureFlags.ALLOW_OKTA_SYNC);
					oSyncEntry.policyEnabled = true;
				}

				const oktassoResponse = resp.results.find(res => {
            return res.resource === oktasso.resource;
        });
        if (oktassoResponse && PolicyEffects[oktassoResponse.effect] === PolicyEffects.ExplicitAllow) {
					let oktaEntry = this.featureStates.get(FeatureFlags.ALLOW_OKTA_SSO);
					oktaEntry.policyEnabled = true;
				}

				const ccnextResponse = resp.results.find(res => {
            return res.resource === ccnext.resource;
        });
				if ( this.xlaunchService.isXLaunchedFromBoss() ) // this is short lived
				{
					let ccEntry = this.featureStates.get(FeatureFlags.ALLOW_CX_CALL_CENTRE);
					ccEntry.policyEnabled = true;
				} else if (ccnextResponse && PolicyEffects[ccnextResponse.effect] === PolicyEffects.ExplicitAllow) {
					let ccEntry = this.featureStates.get(FeatureFlags.ALLOW_CX_CALL_CENTRE);
					ccEntry.policyEnabled = true;
				}

				const subPartnerResponse = resp.results.find(res => {
            return res.resource === subPartner.resource;
        });
        if (subPartnerResponse && PolicyEffects[subPartnerResponse.effect] === PolicyEffects.ExplicitAllow) {
					let spEntry = this.featureStates.get(FeatureFlags.ALLOW_SUB_PARTNER);
					spEntry.policyEnabled = true;
				}

				const apiResponse = resp.results.find(res => {
            return res.resource === api.resource;
        });
        if (apiResponse && PolicyEffects[apiResponse.effect] === PolicyEffects.ExplicitAllow) {
					let apiEntry = this.featureStates.get(FeatureFlags.ALLOW_DEVELOPER_API);
					apiEntry.policyEnabled = true;
				}

				const gsuitedemoResponse = resp.results.find(res => {
            return res.resource === gsuitedemo.resource;
        });
        if (gsuitedemoResponse && PolicyEffects[gsuitedemoResponse.effect] === PolicyEffects.ExplicitAllow) {
					let gsEntry = this.featureStates.get(FeatureFlags.ALLOW_GSUITE);
					gsEntry.policyEnabled = true;
				}

        const managedAccountsResponse = resp.results.find(res => {
          return res.resource === managedAccounts.resource;
				});
				const udpatePoliciesResponse = resp.results.find(res => {
          return res.resource === updateRestrictedAccounts.resource;
				});
				const udpateAccountsResponse = resp.results.find(res => {
          return res.resource === updateRestrictedPolicies.resource;
				});
				if (managedAccountsResponse && udpatePoliciesResponse && udpateAccountsResponse && 
					PolicyEffects[managedAccountsResponse.effect] === PolicyEffects.ExplicitAllow &&
					PolicyEffects[udpatePoliciesResponse.effect] !== PolicyEffects.ExplicitDeny &&
					PolicyEffects[udpateAccountsResponse.effect] !== PolicyEffects.ExplicitDeny) {
						let maEntry = this.featureStates.get(FeatureFlags.ALLOW_MANAGED_ACCOUNTS);
						maEntry.policyEnabled = true;
        }

				// now test the typed policies
				this.testTypedPolicyReply(typedFeatures, resp);

	      return;
			}
			return;
		});
	}

	getTypedFeatures()
	{
		let fFlags : any[] = [];
		FeatureKeyMap.forEach((f) => {
			if ( f.policyKey != TypedPolicyFeatureFlags.NOT_SUPPORTED )
			{
				let feature = this.featureStates.get(f.featureFlag);
				if (feature) 
				{
					let typedPolicyFeature = {
						feature: f.featureFlag,
						policy: {
							resource: "https://mitel.io/auth/feature-flag/features/" + f.policyKey,
							action: "show"
						}
					}
					fFlags.push (typedPolicyFeature);
				}
			}
		});
		return fFlags;
	}

	  /* istanbul ignore next */ 
	
	showADSyncIntegrations(): boolean
	{
		return this.isFeatureEnabled(FeatureFlags.ALLOW_OKTA_SYNC);
	}

	showCCNextIntegration(): boolean
	{
		return this.isFeatureEnabled(FeatureFlags.ALLOW_CX_CALL_CENTRE);
	}

	// only remaining due to the fact it is used in 10s of places
	showMitel5000Integration(): boolean
	{
		return this.isFeatureEnabled(FeatureFlags.ALLOW_MITEL_5000);
	}

	showGSuiteDemoIntegration(): boolean
	{
		return this.isFeatureEnabled(FeatureFlags.ALLOW_GSUITE);
	}

	isSubPartner(): boolean
	{
		return this.isFeatureEnabled(FeatureFlags.ALLOW_SUB_PARTNER);
	}
	
	public isFeatureEnabled( key: string ): boolean 
	{
		const feature = this.featureStates.get(key);
		if (feature) {
			return feature.enabled;
		} else {
			return false;
		}
	}

	public getPackagedFlags(): any[]
	{
		// based on the current known flags, create a object of just 
		let fFlags : any[] = [];
		FeatureKeyMap.forEach((f) => {
			if ( f.policyKey != TypedPolicyFeatureFlags.NOT_SUPPORTED )
        {
          let feature = this.featureStates.get(f.featureFlag);
          if (feature) 
            fFlags.push ({feature: f.featureFlag, enabled: feature.enabled})
        }
		});
		return fFlags;
	}

	// the role changes on 5 occasions
	// 1. When the Partner clicks on an account and assumes role as the account admin 
	// 2. When the Partner uses the company switcher to changes the account they are viewing
	// 3. When the Partner goes back to the Accounts list and back to the partner role
	// 4. When the Partner deletes an account and is reverted back to the partner role to perform the operation
	// 5. When the Partner deactivates an account and is reverted back to the partner role to perform the operation
	// Note: WE ARE ONLY UPDATING THE TYPED policy feature flags and not the legacy policy flags
	// NOTE: If you want to update the legacy flags analysis will be required
	async updateFeatureFlagsOnRoleChange( userClaims: UserClaims )
	{
		// the users claims passed must be for the updated role
		await this.updateTypedFeatureFlagPolicies();

		this.flagsUpdated$.next();
	}

	updateTypedFeatureFlagPolicies()
	{
		// reset the Typed policy feature flags to their default
		FeatureKeyMap.forEach((f) => {
			if ( f.policyKey != TypedPolicyFeatureFlags.NOT_SUPPORTED && !f.ingoreRoleChange)
			{
				let entry = this.featureStates.get(f.featureFlag);
				entry.policyEnabled = false;
				entry.enabled = f.default;
			}
		});

		let feature = {
			resources: [
			],
			types:['feature-flag'] // required for new typed policies
		};

		// get the list of generated resources for typed policies
		let typedFeatures = this.getTypedFeatures();

		for( var typedFeature of typedFeatures )
		{
			feature.resources.push(typedFeature.policy);
		}

		return this.authSvc.getPolicyForFeature(feature).then( async resp => {
			console.log("This is the policy reply", resp);
			// now test the typed policies
			this.testTypedPolicyReply( typedFeatures, resp );

			FeatureKeyMap.forEach((f) => {
				if ( f.policyKey != TypedPolicyFeatureFlags.NOT_SUPPORTED && !f.ingoreRoleChange)
				{
					let entry = this.featureStates.get(f.featureFlag);
					entry.enabled = entry.policyEnabled;
				}
			});
		});
	}

	testTypedPolicyReply( typedFeatures: any, resp: PolicyTestResponse )
	{
		if ( resp && resp.results )
		{
			for( var typedFeature of typedFeatures )
			{
				const testResponse = resp.results.find(res => {
					return res.resource === typedFeature.policy.resource;
				});
				if (testResponse && PolicyEffects[testResponse.effect] === PolicyEffects.ExplicitAllow) {
					let ffEntry = this.featureStates.get(typedFeature.feature);
					if ( ffEntry )
						ffEntry.policyEnabled = true;
				}
			}
		}
	}

	toggleCCIntegrationFeature( account: Account )
	{
		let ccEntry = this.featureStates.get(FeatureFlags.ALLOW_CX_CALL_CENTRE);
		if ( ccEntry.policyEnabled ==  false ) 
		{
			console.log("Add the cc next policy to ", account)
			return this.adminService.addSpecifiedStatementToAccountPolicy(account, CCIntegrationFeatureStatement).then(policy => {
				if ( account['policyId'] == undefined ) // only need to update the account if there was no policy Id
				{
					let dataChanges = {
						policyId: policy.policyId,
					}
		
					return this.adminService.updateAccountWithoutSpinner( account.accountId, dataChanges).then(result => {
						this.accountsService.mergeAccountWithModel(result);
						ccEntry.policyEnabled = true;
						ccEntry.enabled = true;
						return;
					});
				}
				ccEntry.policyEnabled = true;
				ccEntry.enabled = true;
				return;
			})
		}
		else
		{
			console.log("Remove the cc next policy from ", account)
			return this.adminService.removeSpecifiedStatementFromAccountPolicy(account, CCIntegrationFeatureStatement.statementId).then(result => {
				ccEntry.policyEnabled = false;
				ccEntry.enabled = false;
				return;
			})
		}
	}

	async toggleAPIFeature( account: Account ) {
		let apiEntry = this.featureStates.get(FeatureFlags.ALLOW_DEVELOPER_API);
		if (!apiEntry.policyEnabled) {
			console.log("Add the API feature policy to ", account)
			const policy = await this.adminService.addSpecifiedStatementToAccountPolicy(account, APIFeatureStatement);
			if (account['policyId'] == undefined){ // only need to update the account if there was no policy Id			
				let dataChanges = {
					policyId: policy.policyId,
				};

				const updateAccount = await this.adminService.updateAccountWithoutSpinner(account.accountId, dataChanges);
				this.accountsService.mergeAccountWithModel(updateAccount);
				apiEntry.policyEnabled = true;
				apiEntry.enabled = true;
			}else{
				apiEntry.policyEnabled = true;
				apiEntry.enabled = true;
			}
		}
		else {
			console.log("Remove the API feature policy from ", account)
			await this.adminService.removeSpecifiedStatementFromAccountPolicy(account, APIFeatureStatement.statementId);
			apiEntry.policyEnabled = false;
			apiEntry.enabled = false;
		}
	}
}
